import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({});
  const [access, setAccess] = useState(null);

  const loadOptions = useCallback(async () => {
    try {
      const { data } = await api.get("/options");
      setOptions(data);
    } catch (e) {
      console.warn("could not load dropdown options:", e);
    }
    try {
      const { data } = await api.get("/access/me");
      setAccess(data);
    } catch (e) {
      console.warn("could not load access profile:", e);
      setAccess(null);
    }
  }, []);

  const applyUser = useCallback(async (data) => {
    if (data?.token) setToken(data.token);
    setUser(data);
    await loadOptions();
  }, [loadOptions]);

  const checkAuth = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadOptions();
    } catch (e) {
      setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [loadOptions]);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip /me — AuthCallback exchanges session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.requires_2fa) return data;
    await applyUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout request failed, clearing session locally:", e);
    } finally {
      setToken(null);
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser: applyUser, applyUser, loading, login, logout, options, loadOptions, checkAuth,
               access, allowedModules: access?.modules || [], features: access?.features || [] }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
